import type { SaveState } from "../pages/builder";
import { Icon } from "./components";

/** Kuerzt den Server-/Netzwerk-Grund fuer die Kopfzeile — der volle Text steht im `title`. */
function kuerzen(text: string, max = 64): string {
  const t = text.trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

/**
 * A6 (UX-Polish 24.08.2026, `docs/UX-POLISH-UMSETZUNGSPLAN.md` § Paket A) — der Autosave-Zustand
 * des Builders, fest in der Kopfzeile der Seite.
 *
 * ── Warum der Knopf „Als Entwurf speichern" dafuer weichen konnte ────────────────────────────
 * Er versprach etwas, das ohnehin laufend passiert: der Builder speichert seit jeher automatisch
 * (debounced, `builder.tsx`). Ein Knopf daneben laesst genau den Zweifel entstehen, den die
 * Automatik beseitigen sollte — „muss ich das jetzt noch druecken?". Was wirklich gebraucht wird,
 * ist nicht ein zweiter Ausloeser, sondern eine verlaessliche AUSKUNFT: ist mein Stand drin?
 *
 * ── Der Teil, an dem hier alles haengt: `conflict` und `error` ───────────────────────────────
 * `saveState` kennt vier Zustaende, und zwei davon bedeuten **deine Arbeit ist NICHT gespeichert**:
 *   `conflict` — jemand anderes (oder ein zweiter Tab) hat dieselbe Variante inzwischen
 *                geaendert; der eigene Stand wurde NICHT geschrieben. Weiterarbeiten hiesse, auf
 *                einem Stand zu tippen, den es nicht mehr gibt.
 *   `error`    — das Speichern ist fehlgeschlagen (Netz, Worker, 5xx). Alles seit dem letzten
 *                gruenen „Gespeichert" haengt nur im Browser.
 * Beide waren bisher eine dezente Zeile in grau-rotem Kleingedruckten neben den Varianten-Tabs.
 * Genau das ist die Fehlerklasse, vor der `CLAUDE.md` warnt: **ein Vorgang scheitert und sieht
 * aus wie erledigt.** Deshalb sind sie hier nicht nur rot, sondern
 *   · farbig HINTERLEGT statt nur farbig beschriftet (sie heben sich vom Kopfzeilen-Grau ab),
 *   · als KNOPF ausgefuehrt — jeder der beiden traegt seinen Ausweg (neu laden bzw. erneut
 *     speichern), statt den Betreiber ratlos vor einer Meldung stehen zu lassen,
 *   · und sie tragen ein `role="alert"`, damit sie auch vorgelesen werden.
 * Die ruhigen Zustaende (`saving`/`saved`) bleiben bewusst leise — eine Kopfzeile, die staendig
 * blinkt, wird ignoriert, und dann faellt der eine Fall, auf den es ankommt, mit durch.
 *
 * `data-testid="builder-save-status"` und `data-save-state` bleiben WORTGLEICH erhalten: sie sind
 * die einzige textfreie Stelle, an der der E2E-Golden-Path den Autosave-Zustand liest
 * (`apps/e2e/tests/builder-runde-34.spec.ts`).
 *
 * ── `errorMessage` (Speicherfehler-Untersuchung 24.09.2026, Stephanie Reuter) ─────────────────
 * Vorher trug der `error`-Knopf IMMER denselben Text ("Nicht gespeichert – erneut versuchen"),
 * egal ob 401 (Sitzung abgelaufen), 422 (Validierung), 409 event_locked, 5xx oder ein reiner
 * Netzwerkfehler dahinterstand — der wahre Grund stand nur (wenn ueberhaupt) in der `ErrorNote`
 * weiter unten auf der Seite, ausserhalb des Bildausschnitts, den Domenico per WhatsApp
 * verschickt hatte. `errorMessage` traegt jetzt denselben Text, den `builder.tsx` ohnehin schon
 * aus der Server-Antwort liest (`toApiError()`, `apps/web/src/lib/api.ts`) — additiv, ohne den
 * Vier-Zustaende-Vertrag oben anzutasten: fehlt er (z. B. im Test unten), bleibt der alte Text.
 */
export function SpeicherStatus({
  saveState,
  errorMessage,
  onReloadConflict,
  onErneutSpeichern,
}: {
  saveState: SaveState;
  /** Grund des letzten Fehlschlags aus der Server-/Netzwerk-Antwort (nur bei `saveState==="error"`
   *  relevant). Ohne ihn bleibt der frühere, feste Text stehen. */
  errorMessage?: string | null;
  /** Neu laden nach einem Schreib-Konflikt (im Builder: `loadFull(activeId)`). */
  onReloadConflict: () => void;
  /** Denselben Autosave erneut anstossen (im Builder: `save()`), nach einem Fehlschlag. */
  onErneutSpeichern: () => void;
}) {
  return (
    <span
      data-testid="builder-save-status"
      data-save-state={saveState}
      className="flex items-center gap-1.5 font-label-md text-label-md text-on-surface-variant"
    >
      {saveState === "saving" && (
        <>
          <Icon name="sync" className="animate-spin text-[16px]" /> Speichert …
        </>
      )}
      {saveState === "saved" && (
        <>
          <Icon name="cloud_done" className="text-[16px] text-secondary" /> Gespeichert
        </>
      )}
      {saveState === "conflict" && (
        <button
          type="button"
          role="alert"
          data-testid="builder-konflikt-neu-laden"
          onClick={onReloadConflict}
          title="Diese Variante wurde inzwischen an anderer Stelle geändert. Dein letzter Stand wurde NICHT gespeichert – neu laden holt die aktuelle Fassung."
          className="flex items-center gap-1.5 rounded-md bg-error-container px-2.5 py-1 font-semibold text-error hover:bg-error/20 dark:bg-error/25 dark:text-[#e39b92] dark:hover:bg-error/35"
        >
          <Icon name="sync_problem" className="text-[16px]" /> Konflikt – neu laden
        </button>
      )}
      {saveState === "error" && (
        <button
          type="button"
          role="alert"
          data-testid="builder-erneut-speichern"
          onClick={onErneutSpeichern}
          title={
            errorMessage
              ? `Das Speichern ist fehlgeschlagen: ${errorMessage} — Alles seit dem letzten „Gespeichert“ liegt nur im Browser. Klick, um es erneut zu versuchen.`
              : "Das Speichern ist fehlgeschlagen. Alles seit dem letzten „Gespeichert“ liegt nur im Browser – erneut versuchen."
          }
          className="flex items-center gap-1.5 rounded-md bg-error-container px-2.5 py-1 font-semibold text-error hover:bg-error/20 dark:bg-error/25 dark:text-[#e39b92] dark:hover:bg-error/35"
        >
          <Icon name="cloud_off" className="text-[16px]" />
          {errorMessage ? <>Nicht gespeichert – {kuerzen(errorMessage)}</> : <>Nicht gespeichert – erneut versuchen</>}
        </button>
      )}
    </span>
  );
}
